import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { restoreAPI, accountsAPI, oauthAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowUpTrayIcon,
  CalendarIcon,
  PlusCircleIcon,
  ClockIcon,
  PhotoIcon,
  VideoCameraIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  SparklesIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import Footer from './Footer';

const ContentRestore = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Check if user has access
  const hasAccess = ['premium', 'pro', 'agency'].includes(user?.subscription_tier);

  // State for filters and selection
  const [selectedPosts, setSelectedPosts] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [quickFilter, setQuickFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [mediaTypeFilter, setMediaTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const limit = 20;

  // State for restore configuration
  const [targetAccountId, setTargetAccountId] = useState(null);
  const [uploadMode, setUploadMode] = useState('immediate');
  const [intervalMinutes, setIntervalMinutes] = useState(30);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isUpgradingPermissions, setIsUpgradingPermissions] = useState(false);

  // Check if user is admin (allowed to use restore feature)
  const isAdminUser = user?.is_admin;

  // Handle successful connection redirect
  useEffect(() => {
    if (searchParams.get('instagram_connected') === 'true') {
      toast.success('Instagram account connected for restore!');
      queryClient.invalidateQueries(['instagram-accounts']);
      // Clear the search params
      setSearchParams({});
    }
    if (searchParams.get('permissions_upgraded') === 'true') {
      toast.success('Restore permissions granted successfully!');
      queryClient.invalidateQueries(['instagram-accounts']);
      // Auto-select the upgraded account if provided
      const accountId = searchParams.get('account_id');
      if (accountId) {
        setTargetAccountId(parseInt(accountId));
      }
      // Clear the search params
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, queryClient]);

  // Connect a new Instagram account specifically for restore (upload only)
  const handleConnectRestoreAccount = async () => {
    try {
      setIsConnecting(true);
      const response = await oauthAPI.getInstagramAuthUrl('business', 'restore_only');
      if (response.data?.auth_url) {
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      toast.error('Failed to initiate Instagram connection');
      setIsConnecting(false);
    }
  };

  // Upgrade an existing backup account to have restore permissions
  const handleUpgradePermissions = async (accountId) => {
    try {
      setIsUpgradingPermissions(true);
      const response = await oauthAPI.upgradeInstagramPermissions(accountId);
      if (response.data?.auth_url) {
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate permission upgrade');
      setIsUpgradingPermissions(false);
    }
  };

  // Quick filter date calculations
  const getQuickFilterDates = (filter) => {
    const now = new Date();
    let start = null;
    let end = now.toISOString();

    switch (filter) {
      case 'last30':
        start = new Date(now.setDate(now.getDate() - 30)).toISOString();
        break;
      case 'last6months':
        start = new Date(now.setMonth(now.getMonth() - 6)).toISOString();
        break;
      case 'lastyear':
        start = new Date(now.setFullYear(now.getFullYear() - 1)).toISOString();
        break;
      default:
        return { start: null, end: null };
    }

    return { start, end };
  };

  // Apply quick filter
  useEffect(() => {
    if (quickFilter !== 'all' && quickFilter !== 'custom') {
      const dates = getQuickFilterDates(quickFilter);
      setDateRange({
        start: dates.start ? dates.start.split('T')[0] : '',
        end: dates.end ? dates.end.split('T')[0] : ''
      });
    }
  }, [quickFilter]);

  // Build query params
  const queryParams = {
    limit,
    offset: page * limit,
    ...(dateRange.start && { start_date: new Date(dateRange.start).toISOString() }),
    ...(dateRange.end && { end_date: new Date(dateRange.end).toISOString() }),
    ...(mediaTypeFilter !== 'all' && { media_type: mediaTypeFilter }),
    ...(searchQuery && { search: searchQuery })
  };

  // Fetch eligible posts
  const { data: postsData, isLoading: postsLoading, error: postsError } = useQuery({
    queryKey: ['eligible-posts', queryParams],
    queryFn: () => restoreAPI.getEligiblePosts(queryParams).then(res => res.data),
    enabled: hasAccess,
    keepPreviousData: true
  });

  // Fetch Instagram accounts
  const { data: accountsData } = useQuery({
    queryKey: ['instagram-accounts'],
    queryFn: () => accountsAPI.getInstagramAccounts().then(res => res.data),
    enabled: hasAccess
  });

  // Check if selected account has restore permissions
  const selectedAccount = accountsData?.accounts?.find(a => a.id === targetAccountId);
  const selectedAccountHasRestorePermissions = selectedAccount?.account_purpose === 'restore_only' || selectedAccount?.account_purpose === 'both';

  // Fetch existing restore jobs
  const { data: jobsData, isLoading: jobsLoading } = useQuery({
    queryKey: ['restore-jobs'],
    queryFn: () => restoreAPI.getRestoreJobs().then(res => res.data),
    enabled: hasAccess,
    refetchInterval: 10000 // Refresh every 10 seconds
  });

  // Start restore mutation
  const startRestoreMutation = useMutation({
    mutationFn: (data) => restoreAPI.startRestore(data),
    onSuccess: (response) => {
      toast.success(`Restore job started! ${response.data.total_posts} posts will be uploaded.`);
      setSelectedPosts(new Set());
      setSelectAll(false);
      setShowConfirmModal(false);
      queryClient.invalidateQueries(['restore-jobs']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to start restore job');
    }
  });

  // Cancel job mutation
  const cancelJobMutation = useMutation({
    mutationFn: (jobId) => restoreAPI.cancelRestoreJob(jobId),
    onSuccess: () => {
      toast.success('Restore job cancelled');
      queryClient.invalidateQueries(['restore-jobs']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to cancel job');
    }
  });

  // Pause/Resume mutations
  const pauseJobMutation = useMutation({
    mutationFn: (jobId) => restoreAPI.pauseRestoreJob(jobId),
    onSuccess: () => {
      toast.success('Restore job paused');
      queryClient.invalidateQueries(['restore-jobs']);
    }
  });

  const resumeJobMutation = useMutation({
    mutationFn: (jobId) => restoreAPI.resumeRestoreJob(jobId),
    onSuccess: () => {
      toast.success('Restore job resumed');
      queryClient.invalidateQueries(['restore-jobs']);
    }
  });

  // Handle post selection
  const handlePostSelect = (postId) => {
    const newSelected = new Set(selectedPosts);
    if (newSelected.has(postId)) {
      newSelected.delete(postId);
    } else {
      newSelected.add(postId);
    }
    setSelectedPosts(newSelected);
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedPosts(new Set());
    } else {
      const allIds = postsData?.posts?.map(p => p.id) || [];
      setSelectedPosts(new Set(allIds));
    }
    setSelectAll(!selectAll);
  };

  // Calculate estimated completion time
  const getEstimatedCompletion = () => {
    const count = selectedPosts.size || (dateRange.start && dateRange.end ? postsData?.total : 0);
    if (!count) return null;

    const interval = uploadMode === 'scheduled' ? intervalMinutes : 3;
    const totalMinutes = count * interval;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `~${days} day${days > 1 ? 's' : ''}`;
    } else if (hours > 0) {
      return `~${hours}h ${minutes}m`;
    } else {
      return `~${minutes} minutes`;
    }
  };

  // Handle start restore
  const handleStartRestore = () => {
    if (!targetAccountId) {
      toast.error('Please select a target Instagram account');
      return;
    }

    const requestData = {
      target_account_id: targetAccountId,
      upload_mode: uploadMode,
      interval_minutes: uploadMode === 'scheduled' ? intervalMinutes : undefined
    };

    if (selectedPosts.size > 0) {
      requestData.post_ids = Array.from(selectedPosts);
    } else if (dateRange.start && dateRange.end) {
      requestData.date_range = {
        start_date: new Date(dateRange.start).toISOString(),
        end_date: new Date(dateRange.end).toISOString()
      };
    } else {
      toast.error('Please select posts or a date range');
      return;
    }

    startRestoreMutation.mutate(requestData);
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/20 text-green-400';
      case 'running': return 'bg-blue-500/20 text-blue-400';
      case 'paused': return 'bg-yellow-500/20 text-yellow-400';
      case 'failed': return 'bg-red-500/20 text-red-400';
      case 'cancelled': return 'bg-gray-500/20 text-gray-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  // If user doesn't have access, show upgrade prompt
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-12 border border-white/20 text-center">
            <LockClosedIcon className="h-16 w-16 text-yellow-400 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-white mb-4">Content Restore</h1>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Re-upload your backed-up content to a new Instagram account. Perfect for recovering from
              account bans or migrating to a new profile.
            </p>
            <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-2xl p-6 border border-yellow-500/20 mb-8">
              <p className="text-yellow-300 font-medium mb-2">Premium Feature</p>
              <p className="text-gray-300 text-sm">
                Content Restore is available for Premium, Pro, and Agency subscribers.
              </p>
            </div>
            <button
              onClick={() => navigate('/pricing')}
              className="px-8 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-purple-700 transition-all"
            >
              Upgrade Now
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Content Restore</h1>
          <p className="text-gray-300">
            Re-upload your backed-up Instagram content to a new account
          </p>
        </div>

        {/* Active Jobs Section */}
        {jobsData?.jobs?.some(j => ['running', 'paused', 'pending'].includes(j.status)) && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">Active Restore Jobs</h2>
            <div className="space-y-4">
              {jobsData.jobs
                .filter(j => ['running', 'paused', 'pending'].includes(j.status))
                .map(job => (
                  <div
                    key={job.id}
                    className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(job.status)}`}>
                          {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                        </span>
                        <span className="text-white">
                          {job.target_account_username ? `@${job.target_account_username}` : 'Unknown Account'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {job.status === 'running' && (
                          <button
                            onClick={() => pauseJobMutation.mutate(job.id)}
                            className="p-2 text-yellow-400 hover:bg-yellow-500/20 rounded-lg transition-colors"
                            title="Pause"
                          >
                            <PauseIcon className="h-5 w-5" />
                          </button>
                        )}
                        {job.status === 'paused' && (
                          <button
                            onClick={() => resumeJobMutation.mutate(job.id)}
                            className="p-2 text-green-400 hover:bg-green-500/20 rounded-lg transition-colors"
                            title="Resume"
                          >
                            <PlayIcon className="h-5 w-5" />
                          </button>
                        )}
                        <button
                          onClick={() => cancelJobMutation.mutate(job.id)}
                          className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                          title="Cancel"
                        >
                          <StopIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Progress bar */}
                    <div className="mb-2">
                      <div className="flex justify-between text-sm text-gray-400 mb-1">
                        <span>{job.completed_posts} of {job.total_posts} completed</span>
                        <span>{Math.round(job.progress_percentage)}%</span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-pink-500 to-purple-600 transition-all"
                          style={{ width: `${job.progress_percentage}%` }}
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">
                        {job.failed_posts > 0 && (
                          <span className="text-red-400">{job.failed_posts} failed</span>
                        )}
                      </span>
                      {job.estimated_completion && (
                        <span className="text-gray-400">
                          Est. completion: {formatDate(job.estimated_completion)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Post Selection */}
          <div className="lg:col-span-2 space-y-6">
            {/* Filters */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <FunnelIcon className="h-5 w-5 mr-2" />
                Filter Posts
              </h3>
              
              {/* Quick Filters */}
              <div className="flex flex-wrap gap-2 mb-4">
                {[
                  { id: 'all', label: 'All Time' },
                  { id: 'last30', label: 'Last 30 Days' },
                  { id: 'last6months', label: 'Last 6 Months' },
                  { id: 'lastyear', label: 'Last Year' },
                  { id: 'custom', label: 'Custom Range' }
                ].map(filter => (
                  <button
                    key={filter.id}
                    onClick={() => setQuickFilter(filter.id)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      quickFilter === filter.id
                        ? 'bg-pink-500 text-white'
                        : 'bg-white/10 text-gray-300 hover:bg-white/20'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
              
              {/* Custom Date Range */}
              {quickFilter === 'custom' && (
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                      className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-pink-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">End Date</label>
                    <input
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                      className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-pink-500"
                    />
                  </div>
                </div>
              )}
              
              {/* Media Type Filter */}
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'all', label: 'All Types' },
                  { id: 'IMAGE', label: 'Photos', icon: PhotoIcon },
                  { id: 'VIDEO', label: 'Videos', icon: VideoCameraIcon }
                ].map(type => (
                  <button
                    key={type.id}
                    onClick={() => setMediaTypeFilter(type.id)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center transition-all ${
                      mediaTypeFilter === type.id
                        ? 'bg-purple-500 text-white'
                        : 'bg-white/10 text-gray-300 hover:bg-white/20'
                    }`}
                  >
                    {type.icon && <type.icon className="h-4 w-4 mr-2" />}
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Posts Grid */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">
                  Backed-up Posts ({postsData?.total || 0})
                </h3>
                <button
                  onClick={handleSelectAll}
                  className="text-sm text-pink-400 hover:text-pink-300"
                >
                  {selectAll ? 'Deselect All' : 'Select All on Page'}
                </button>
              </div>
              
              {postsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <ArrowPathIcon className="h-8 w-8 text-pink-500 animate-spin" />
                </div>
              ) : postsError ? (
                <div className="text-center py-12 text-red-400">
                  <ExclamationTriangleIcon className="h-12 w-12 mx-auto mb-4" />
                  <p>Failed to load posts</p>
                </div>
              ) : postsData?.posts?.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <PhotoIcon className="h-12 w-12 mx-auto mb-4" />
                  <p>No backed-up posts found</p>
                  <p className="text-sm mt-2">Adjust your filters or backup more content</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                    {postsData?.posts?.map(post => (
                      <div
                        key={post.id}
                        onClick={() => handlePostSelect(post.id)}
                        className={`relative aspect-square rounded-xl overflow-hidden cursor-pointer transition-all ${
                          selectedPosts.has(post.id)
                            ? 'ring-2 ring-pink-500 ring-offset-2 ring-offset-slate-900'
                            : 'hover:opacity-80'
                        }`}
                      >
                        <img
                          src={post.thumbnail_url || post.media_url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                        {post.media_type === 'VIDEO' && (
                          <div className="absolute top-2 right-2">
                            <VideoCameraIcon className="h-5 w-5 text-white drop-shadow-lg" />
                          </div>
                        )}
                        {selectedPosts.has(post.id) && (
                          <div className="absolute inset-0 bg-pink-500/30 flex items-center justify-center">
                            <CheckCircleIcon className="h-8 w-8 text-white" />
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                          <p className="text-xs text-white truncate">
                            {formatDate(post.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Pagination */}
                  {postsData?.total > limit && (
                    <div className="flex items-center justify-center mt-6 space-x-4">
                      <button
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        className="p-2 rounded-lg bg-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20"
                      >
                        <ChevronLeftIcon className="h-5 w-5" />
                      </button>
                      <span className="text-gray-400">
                        Page {page + 1} of {Math.ceil(postsData.total / limit)}
                      </span>
                      <button
                        onClick={() => setPage(p => p + 1)}
                        disabled={(page + 1) * limit >= postsData.total}
                        className="p-2 rounded-lg bg-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20"
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Right Column - Restore Configuration */}
          <div className="space-y-6">
            {/* Target Account */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <h3 className="text-lg font-semibold text-white mb-4">Target Account</h3>
              <p className="text-sm text-gray-400 mb-4">
                Select the Instagram account where you want to upload the content
              </p>
              
              <div className="space-y-2">
                {accountsData?.accounts?.map(account => (
                  <button
                    key={account.id}
                    onClick={() => setTargetAccountId(account.id)}
                    className={`w-full p-3 rounded-xl text-left transition-all ${
                      targetAccountId === account.id
                        ? 'bg-pink-500/20 border-pink-500'
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                    } border`}
                  >
                    <div className="flex items-center space-x-3">
                      {account.profile_picture_url ? (
                        <img
                          src={account.profile_picture_url}
                          alt=""
                          className="w-10 h-10 rounded-full"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-purple-600" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <p className="text-white font-medium">@{account.username}</p>
                          {account.account_purpose === 'restore_only' && (
                            <span className="px-1.5 py-0.5 text-xs bg-purple-500/30 text-purple-300 rounded-full">
                              Restore Only
                            </span>
                          )}
                          {account.account_purpose === 'both' && (
                            <span className="px-1.5 py-0.5 text-xs bg-green-500/30 text-green-300 rounded-full">
                              Backup + Restore
                            </span>
                          )}
                          {account.account_purpose === 'backup' && (
                            <span className="px-1.5 py-0.5 text-xs bg-yellow-500/30 text-yellow-300 rounded-full">
                              Needs Permission
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-400">{account.follower_count?.toLocaleString()} followers</p>
                      </div>
                      {targetAccountId === account.id && (
                        <CheckCircleIcon className="h-5 w-5 text-pink-400 ml-auto" />
                      )}
                    </div>
                  </button>
                ))}
                
                {/* Connect New Account for Restore */}
                <button
                  onClick={handleConnectRestoreAccount}
                  disabled={isConnecting}
                  className="w-full p-3 rounded-xl text-left transition-all bg-white/5 border border-dashed border-white/20 hover:bg-white/10 hover:border-white/30"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500/30 to-pink-500/30 flex items-center justify-center">
                      <PlusCircleIcon className="h-6 w-6 text-purple-300" />
                    </div>
                    <div>
                      <p className="text-white font-medium">
                        {isConnecting ? 'Connecting...' : 'Connect New Account'}
                      </p>
                      <p className="text-sm text-gray-400">For restore/upload only (no backups)</p>
                    </div>
                  </div>
                </button>
              </div>
              
              {!accountsData?.accounts?.length && (
                <p className="text-center text-sm text-gray-500 mt-3">
                  Connect an existing account or add a new one for restore
                </p>
              )}
              
              {/* Warning for backup accounts needing permission upgrade */}
              {selectedAccount && selectedAccount.account_purpose === 'backup' && (
                <div className="mt-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-yellow-300 font-medium text-sm">Additional Permission Required</p>
                      <p className="text-gray-300 text-xs mt-1">
                        This account was connected for backup only. To restore content, you need to grant additional permissions.
                      </p>
                      <button
                        onClick={() => handleUpgradePermissions(selectedAccount.id)}
                        disabled={isUpgradingPermissions}
                        className="mt-3 w-full py-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 font-medium rounded-lg transition-all text-sm disabled:opacity-50"
                      >
                        {isUpgradingPermissions ? 'Redirecting...' : 'Grant Restore Permission'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Upload Settings */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <h3 className="text-lg font-semibold text-white mb-4">Upload Settings</h3>
              
              {/* Upload Mode */}
              <div className="mb-6">
                <label className="block text-sm text-gray-400 mb-2">Upload Mode</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setUploadMode('immediate')}
                    className={`p-4 rounded-xl text-center transition-all ${
                      uploadMode === 'immediate'
                        ? 'bg-pink-500/20 border-pink-500'
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                    } border`}
                  >
                    <SparklesIcon className="h-6 w-6 mx-auto mb-2 text-pink-400" />
                    <p className="text-white font-medium">Immediate</p>
                    <p className="text-xs text-gray-400 mt-1">Upload ASAP</p>
                  </button>
                  <button
                    onClick={() => setUploadMode('scheduled')}
                    className={`p-4 rounded-xl text-center transition-all ${
                      uploadMode === 'scheduled'
                        ? 'bg-pink-500/20 border-pink-500'
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                    } border`}
                  >
                    <ClockIcon className="h-6 w-6 mx-auto mb-2 text-purple-400" />
                    <p className="text-white font-medium">Scheduled</p>
                    <p className="text-xs text-gray-400 mt-1">Spread over time</p>
                  </button>
                </div>
              </div>
              
              {/* Interval Slider (for scheduled mode) */}
              {uploadMode === 'scheduled' && (
                <div className="mb-6">
                  <label className="block text-sm text-gray-400 mb-2">
                    Interval Between Posts: {intervalMinutes} minutes
                  </label>
                  <input
                    type="range"
                    min="5"
                    max="240"
                    step="5"
                    value={intervalMinutes}
                    onChange={(e) => setIntervalMinutes(parseInt(e.target.value))}
                    className="w-full accent-pink-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>5 min</span>
                    <span>1 hour</span>
                    <span>4 hours</span>
                  </div>
                </div>
              )}
              
              {/* Summary */}
              <div className="bg-white/5 rounded-xl p-4 mb-6">
                <h4 className="text-sm font-medium text-white mb-2">Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Posts to restore:</span>
                    <span className="text-white font-medium">
                      {selectedPosts.size || (dateRange.start && dateRange.end ? `All in range` : '0')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Est. completion:</span>
                    <span className="text-white font-medium">
                      {getEstimatedCompletion() || '-'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Start Button */}
              {!isAdminUser ? (
                <div className="space-y-2">
                  <button
                    disabled={true}
                    className="w-full py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-semibold rounded-xl opacity-60 cursor-not-allowed flex items-center justify-center"
                  >
                    <ClockIcon className="h-5 w-5 mr-2" />
                    Coming Soon
                  </button>
                  <p className="text-center text-xs text-gray-400">
                    Content restore feature is coming soon. Stay tuned!
                  </p>
                </div>
              ) : (
                <button
                  onClick={() => setShowConfirmModal(true)}
                  disabled={
                    !targetAccountId ||
                    !selectedAccountHasRestorePermissions ||
                    (selectedPosts.size === 0 && (!dateRange.start || !dateRange.end)) ||
                    startRestoreMutation.isLoading
                  }
                  className="w-full py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {startRestoreMutation.isLoading ? (
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                      {!selectedAccountHasRestorePermissions && targetAccountId ? 'Grant Permission First' : 'Start Restore'}
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Recent Jobs */}
            {jobsData?.jobs?.length > 0 && (
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                <h3 className="text-lg font-semibold text-white mb-4">Recent Jobs</h3>
                <div className="space-y-3">
                  {jobsData.jobs.slice(0, 5).map(job => (
                    <div
                      key={job.id}
                      className="bg-white/5 rounded-xl p-3"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                          {job.status}
                        </span>
                        <span className="text-xs text-gray-400">
                          {formatDate(job.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-white">
                        {job.completed_posts}/{job.total_posts} posts
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 rounded-2xl p-6 max-w-md w-full border border-white/20">
              <h3 className="text-xl font-semibold text-white mb-4">Confirm Restore</h3>
              <p className="text-gray-300 mb-6">
                You're about to restore{' '}
                <span className="text-pink-400 font-medium">
                  {selectedPosts.size || 'all selected'}
                </span>{' '}
                posts to{' '}
                <span className="text-pink-400 font-medium">
                  @{accountsData?.accounts?.find(a => a.id === targetAccountId)?.username}
                </span>
              </p>
              
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-yellow-300 font-medium">Important</p>
                    <p className="text-gray-300 mt-1">
                      Posts will be uploaded with their original captions and hashtags. 
                      This may take {getEstimatedCompletion()} to complete.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 py-2 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStartRestore}
                  disabled={startRestoreMutation.isLoading}
                  className="flex-1 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-purple-700 transition-all disabled:opacity-50"
                >
                  {startRestoreMutation.isLoading ? 'Starting...' : 'Start Restore'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ContentRestore;

